import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { suppliersAPI } from '@/lib/api-client'
import { formatDocument } from '@/lib/utils'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SupplierFormDialog } from './SupplierFormDialog'
import { MaterialFormDialog } from './MaterialFormDialog'
import { LinkMaterialDialog } from './LinkMaterialDialog'
import { PurchaseOrderFormDialog } from './PurchaseOrderFormDialog'
import { InstallmentsDialog } from './InstallmentsDialog'
import {
  ArrowLeft,
  Edit,
  Trash2,
  Star,
  Phone,
  Mail,
  MapPin,
  User,
  Truck,
  Package,
  ShoppingCart,
  Loader2,
  Plus,
  Unlink,
  ChevronDown,
  ChevronUp,
  TrendingDown,
  Clock,
  Globe,
  CreditCard,
  Link2,
} from 'lucide-react'

const DATASOURCE_LABELS: Record<string, string> = {
  MANUAL: 'Manual',
  AUTO_CNPJ: 'CNPJ Auto',
  AUTO_DETECTION: 'Auto Detectado',
}

const DATASOURCE_COLORS: Record<string, string> = {
  MANUAL: 'bg-neutral-100 text-neutral-600',
  AUTO_CNPJ: 'bg-blue-100 text-blue-700',
  AUTO_DETECTION: 'bg-purple-100 text-purple-700',
}

const TYPE_LABELS: Record<string, string> = {
  MATERIALS: 'Materiais',
  SERVICES: 'Serviços',
  BOTH: 'Ambos',
}

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  ORDERED: 'Pedido Feito',
  DELIVERED: 'Entregue',
  CANCELLED: 'Cancelado',
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  ORDERED: 'bg-purple-100 text-purple-700',
  DELIVERED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
}

const NEXT_STATUS: Record<string, string | null> = {
  PENDING: 'APPROVED',
  APPROVED: 'ORDERED',
  ORDERED: 'DELIVERED',
  DELIVERED: null,
  CANCELLED: null,
}

const NEXT_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Aprovar',
  APPROVED: 'Marcar como Pedido',
  ORDERED: 'Marcar como Entregue',
}

function renderStars(rating: number) {
  const stars = []
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`h-4 w-4 ${
          i <= rating
            ? 'fill-amber-400 text-amber-400'
            : 'text-neutral-300'
        }`}
      />
    )
  }
  return stars
}

export function SupplierDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canEdit = usePermission('suppliers:edit')
  const canDelete = usePermission('suppliers:delete')
  const canCreatePO = usePermission('purchases:create')
  const canEditPO = usePermission('purchases:edit')

  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showMaterialDialog, setShowMaterialDialog] = useState(false)
  const [showLinkMaterialDialog, setShowLinkMaterialDialog] = useState(false)
  const [showPODialog, setShowPODialog] = useState(false)
  const [expandedPO, setExpandedPO] = useState<string | null>(null)
  const [statusChangeDialog, setStatusChangeDialog] = useState<{
    orderId: string
    newStatus: string
    orderNumber: string
  } | null>(null)
  // Queries
  const { data: supplier, isLoading } = useQuery({
    queryKey: ['supplier', id],
    queryFn: () => suppliersAPI.getById(id!),
    enabled: !!id,
  })

  const { data: supplierMaterials } = useQuery({
    queryKey: ['supplier-materials', id],
    queryFn: () => suppliersAPI.listSupplierMaterials(id!),
    enabled: !!id,
  })

  const { data: purchaseOrdersData } = useQuery({
    queryKey: ['supplier-purchase-orders', id],
    queryFn: () => suppliersAPI.listPurchaseOrders({ supplierId: id, limit: 50 }),
    enabled: !!id,
  })

  const { data: financialSummary } = useQuery({
    queryKey: ['supplier-financial', id],
    queryFn: () => suppliersAPI.getFinancialSummary(id!),
    enabled: !!id,
  })


  // Mutations
  const deleteMutation = useMutation({
    mutationFn: () => suppliersAPI.delete(id!),
    onSuccess: (data: any) => {
      toast.success(data.message)
      queryClient.invalidateQueries({ queryKey: ['suppliers'] })
      navigate('/suppliers')
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const unlinkMaterialMutation = useMutation({
    mutationFn: (materialId: string) => suppliersAPI.unlinkMaterial(id!, materialId),
    onSuccess: () => {
      toast.success('Material desvinculado!')
      queryClient.invalidateQueries({ queryKey: ['supplier-materials', id] })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const statusMutation = useMutation({
    mutationFn: ({ orderId, status }: {
      orderId: string
      status: string
    }) =>
      suppliersAPI.updatePurchaseOrderStatus(orderId, { status }),
    onSuccess: () => {
      toast.success('Status do pedido atualizado!')
      queryClient.invalidateQueries({ queryKey: ['supplier-purchase-orders', id] })
      queryClient.invalidateQueries({ queryKey: ['supplier-financial', id] })
      setStatusChangeDialog(null)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const cancelPOMutation = useMutation({
    mutationFn: (orderId: string) =>
      suppliersAPI.updatePurchaseOrderStatus(orderId, { status: 'CANCELLED' }),
    onSuccess: () => {
      toast.success('Pedido cancelado!')
      queryClient.invalidateQueries({ queryKey: ['supplier-purchase-orders', id] })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const deletePOMutation = useMutation({
    mutationFn: (orderId: string) => suppliersAPI.deletePurchaseOrder(orderId),
    onSuccess: () => {
      toast.success('Pedido excluído!')
      queryClient.invalidateQueries({ queryKey: ['supplier-purchase-orders', id] })
    },
    onError: (error: Error) => toast.error(error.message),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!supplier) {
    return (
      <div className="text-center py-24">
        <p className="text-neutral-500">Fornecedor não encontrado</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/suppliers')}>
          Voltar
        </Button>
      </div>
    )
  }

  const purchaseOrders = purchaseOrdersData?.data || []
  const materials = supplierMaterials || []

  const handleStatusChange = (orderId: string, newStatus: string, orderNumber: string) => {
    if (newStatus === 'DELIVERED') {
      setStatusChangeDialog({ orderId, newStatus, orderNumber })
    } else {
      statusMutation.mutate({ orderId, status: newStatus })
    }
  }

  const confirmStatusChange = () => {
    if (!statusChangeDialog) return
    statusMutation.mutate({
      orderId: statusChangeDialog.orderId,
      status: statusChangeDialog.newStatus,
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/suppliers')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Truck className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold text-neutral-900">{supplier.name}</h1>
              <Badge variant={supplier.isActive ? 'default' : 'secondary'}>
                {supplier.isActive ? 'Ativo' : 'Inativo'}
              </Badge>
              {supplier.dataSource && supplier.dataSource !== 'MANUAL' && (
                <Badge variant="outline" className={DATASOURCE_COLORS[supplier.dataSource] || ''}>
                  {DATASOURCE_LABELS[supplier.dataSource] || supplier.dataSource}
                </Badge>
              )}
            </div>
            <p className="mt-1 text-neutral-500">{formatDocument(supplier.document)}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => setShowEditDialog(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Editar
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              className="text-destructive border-destructive hover:bg-destructive/10"
              onClick={() => {
                if (confirm('Tem certeza que deseja excluir/desativar este fornecedor?')) {
                  deleteMutation.mutate()
                }
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info">
        <div className="overflow-x-auto">
          <TabsList className="w-max min-w-full sm:w-auto">
            <TabsTrigger value="info">Informações</TabsTrigger>
            <TabsTrigger value="materials">Materiais</TabsTrigger>
            <TabsTrigger value="orders">Pedidos</TabsTrigger>
            <TabsTrigger value="financial">Financeiro</TabsTrigger>
          </TabsList>
        </div>

        {/* ==================== TAB: Informações ==================== */}
        <TabsContent value="info" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Basic info card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Dados Básicos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <Truck className="h-4 w-4 text-neutral-400" />
                  <div>
                    <p className="text-sm text-neutral-500">Tipo</p>
                    <p className="font-medium">{TYPE_LABELS[supplier.type]}</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center gap-3">
                  <Star className="h-4 w-4 text-neutral-400" />
                  <div>
                    <p className="text-sm text-neutral-500">Avaliação</p>
                    <div className="flex items-center gap-1">
                      {renderStars(supplier.rating || 0)}
                      <span className="ml-1 text-sm">({supplier.rating || 0}/5)</span>
                    </div>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center gap-3">
                  <ShoppingCart className="h-4 w-4 text-neutral-400" />
                  <div>
                    <p className="text-sm text-neutral-500">Pedidos de Compra</p>
                    <p className="font-medium">{supplier._count?.purchaseOrders || 0}</p>
                  </div>
                </div>
                <Separator />
                <div className="flex items-center gap-3">
                  <Package className="h-4 w-4 text-neutral-400" />
                  <div>
                    <p className="text-sm text-neutral-500">Materiais Vinculados</p>
                    <p className="font-medium">{supplier._count?.materials || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contacts card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Contato</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Company-level fields */}
                {supplier.contacts?.address && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-neutral-400 shrink-0" />
                    <div>
                      <p className="text-sm text-neutral-500">Endereço</p>
                      <p className="font-medium">{supplier.contacts.address}</p>
                    </div>
                  </div>
                )}
                {supplier.contacts?.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="h-4 w-4 text-neutral-400 shrink-0" />
                    <div>
                      <p className="text-sm text-neutral-500">Website</p>
                      <a
                        href={supplier.contacts.website.startsWith('http') ? supplier.contacts.website : `https://${supplier.contacts.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 hover:underline"
                      >
                        {supplier.contacts.website}
                      </a>
                    </div>
                  </div>
                )}

                {/* Contact list — new format */}
                {(() => {
                  const contacts = supplier.contacts
                  const list: any[] = contacts?.list?.length
                    ? contacts.list
                    : contacts?.contactPerson
                    ? [{ name: contacts.contactPerson, phone: contacts.phone, email: contacts.email }]
                    : []

                  if (list.length === 0 && !contacts?.address && !contacts?.website) {
                    return <p className="text-sm text-neutral-400">Nenhum contato informado</p>
                  }

                  return list.map((c: any, idx: number) => (
                    <div key={idx}>
                      {(idx > 0 || contacts?.address || contacts?.website) && <Separator className="mb-3" />}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-neutral-400 shrink-0" />
                          <div>
                            <p className="font-medium text-sm">{c.name}</p>
                            {c.role && <p className="text-xs text-neutral-500">{c.role}</p>}
                          </div>
                        </div>
                        {c.phone && (
                          <div className="flex items-center gap-2 pl-6">
                            <Phone className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                            <p className="text-sm">{c.phone}</p>
                          </div>
                        )}
                        {c.email && (
                          <div className="flex items-center gap-2 pl-6">
                            <Mail className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                            <p className="text-sm">{c.email}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                })()}
              </CardContent>
            </Card>
          </div>

          {/* Dados da Receita Federal */}
          {supplier.metadata && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Dados da Receita Federal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {supplier.metadata.razaoSocial && (
                  <div>
                    <span className="text-neutral-500">Razao Social:</span>{' '}
                    <span className="font-medium">{supplier.metadata.razaoSocial}</span>
                  </div>
                )}
                {supplier.metadata.cnaeFiscalDescricao && (
                  <div>
                    <span className="text-neutral-500">Atividade:</span>{' '}
                    <span className="font-medium">{supplier.metadata.cnaeFiscalDescricao}</span>
                  </div>
                )}
                {supplier.metadata.porte && (
                  <div>
                    <span className="text-neutral-500">Porte:</span>{' '}
                    <span className="font-medium">{supplier.metadata.porte}</span>
                  </div>
                )}
                {supplier.metadata.capitalSocial != null && (
                  <div>
                    <span className="text-neutral-500">Capital Social:</span>{' '}
                    <span className="font-medium">
                      R$ {Number(supplier.metadata.capitalSocial).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {supplier.metadata.descricaoSituacaoCadastral && (
                  <div>
                    <span className="text-neutral-500">Situacao:</span>{' '}
                    <Badge variant="outline">{supplier.metadata.descricaoSituacaoCadastral}</Badge>
                  </div>
                )}
                {supplier.metadata.naturezaJuridica && (
                  <div>
                    <span className="text-neutral-500">Natureza Juridica:</span>{' '}
                    <span className="font-medium">{supplier.metadata.naturezaJuridica}</span>
                  </div>
                )}
                {supplier.metadata.dataInicioAtividade && (
                  <div>
                    <span className="text-neutral-500">Inicio Atividade:</span>{' '}
                    <span className="font-medium">{new Date(supplier.metadata.dataInicioAtividade).toLocaleDateString('pt-BR')}</span>
                  </div>
                )}
                {supplier.metadata.qsa?.length > 0 && (
                  <div>
                    <span className="text-neutral-500">Socios:</span>
                    <ul className="ml-4 mt-1 list-disc">
                      {supplier.metadata.qsa.map((s: any, i: number) => (
                        <li key={i}>{s.nome}{s.qualificacao ? ` - ${s.qualificacao}` : ''}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ==================== TAB: Materiais ==================== */}
        <TabsContent value="materials" className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-lg font-medium">Materiais do Fornecedor</h3>
            {canEdit && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowMaterialDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Novo Material
                </Button>
                <Button onClick={() => setShowLinkMaterialDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Vincular Material
                </Button>
              </div>
            )}
          </div>

          {materials.length === 0 ? (
            <div className="text-center py-12 border rounded-lg bg-neutral-50">
              <Package className="mx-auto h-12 w-12 text-neutral-300" />
              <p className="mt-4 text-neutral-500">Nenhum material vinculado</p>
              {canEdit && (
                <Button className="mt-4" variant="outline" onClick={() => setShowLinkMaterialDialog(true)}>
                  Vincular Primeiro Material
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Material</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Unidade</TableHead>
                  <TableHead className="text-right">Preço Fornecedor</TableHead>
                  <TableHead className="text-right">Preço Catálogo</TableHead>
                  {canEdit && <TableHead className="w-10"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials.map((link: any) => {
                  const diff = link.price - link.material.currentPrice
                  const diffPercent = link.material.currentPrice > 0
                    ? ((diff / link.material.currentPrice) * 100).toFixed(1)
                    : '0'
                  return (
                    <TableRow key={link.id}>
                      <TableCell className="font-medium">{link.material.name}</TableCell>
                      <TableCell>{link.material.category}</TableCell>
                      <TableCell>{link.material.unit}</TableCell>
                      <TableCell className="text-right">R$ {link.price.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        R$ {link.material.currentPrice.toFixed(2)}
                        {diff !== 0 && (
                          <span className={`ml-1 text-xs ${diff > 0 ? 'text-red-500' : 'text-green-500'}`}>
                            ({diff > 0 ? '+' : ''}{diffPercent}%)
                          </span>
                        )}
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={() => {
                              if (confirm('Desvincular este material?')) {
                                unlinkMaterialMutation.mutate(link.material.id)
                              }
                            }}
                          >
                            <Unlink className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </TabsContent>

        {/* ==================== TAB: Pedidos ==================== */}
        <TabsContent value="orders" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Pedidos de Compra</h3>
            {canCreatePO && (
              <Button onClick={() => setShowPODialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Pedido
              </Button>
            )}
          </div>

          {purchaseOrders.length === 0 ? (
            <div className="text-center py-12 border rounded-lg bg-neutral-50">
              <ShoppingCart className="mx-auto h-12 w-12 text-neutral-300" />
              <p className="mt-4 text-neutral-500">Nenhum pedido de compra</p>
              {canCreatePO && (
                <Button className="mt-4" variant="outline" onClick={() => setShowPODialog(true)}>
                  Criar Primeiro Pedido
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {purchaseOrders.map((order: any) => (
                <Card key={order.id}>
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer"
                    onClick={() => setExpandedPO(expandedPO === order.id ? null : order.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="font-medium">{order.orderNumber}</p>
                        <p className="text-sm text-neutral-500">{order.project?.name}</p>
                      </div>
                      <Badge variant="outline" className={STATUS_COLORS[order.status]}>
                        {STATUS_LABELS[order.status]}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold">R$ {order.totalAmount.toFixed(2)}</p>
                        <p className="text-xs text-neutral-500">
                          {new Date(order.orderDate).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      {expandedPO === order.id ? (
                        <ChevronUp className="h-4 w-4 text-neutral-400" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-neutral-400" />
                      )}
                    </div>
                  </div>

                  {expandedPO === order.id && (
                    <div className="border-t px-4 pb-4 pt-3 space-y-3">
                      {/* Load full order details */}
                      <PurchaseOrderDetails
                        orderId={order.id}
                        status={order.status}
                        orderNumber={order.orderNumber}
                        canEdit={canEditPO}
                        onStatusChange={handleStatusChange}
                        onCancel={(orderId) => {
                          if (confirm('Cancelar este pedido?')) {
                            cancelPOMutation.mutate(orderId)
                          }
                        }}
                        onDelete={(orderId) => {
                          if (confirm('Excluir este pedido?')) {
                            deletePOMutation.mutate(orderId)
                          }
                        }}
                      />
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ==================== TAB: Financeiro ==================== */}
        <TabsContent value="financial" className="space-y-6">
          {/* Summary cards */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-red-100 p-2">
                    <TrendingDown className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Total Gasto</p>
                    <p className="text-2xl font-bold text-neutral-900">
                      R$ {(financialSummary?.totalGasto || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-amber-100 p-2">
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-neutral-500">Pedidos Pendentes</p>
                    <p className="text-2xl font-bold text-neutral-900">
                      R$ {(financialSummary?.pedidosPendentes || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent transactions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Transações Recentes</CardTitle>
            </CardHeader>
            <CardContent>
              {!financialSummary?.transacoesRecentes?.length ? (
                <p className="text-sm text-neutral-400 py-4 text-center">
                  Nenhuma transação vinculada
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Projeto</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {financialSummary.transacoesRecentes.map((tx: any) => (
                      <TableRow key={tx.id}>
                        <TableCell>{tx.description}</TableCell>
                        <TableCell>{tx.project?.name || '-'}</TableCell>
                        <TableCell>
                          {new Date(tx.date).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-right font-medium text-red-600">
                          R$ {tx.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {showEditDialog && (
        <SupplierFormDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          supplier={supplier}
        />
      )}

      <MaterialFormDialog
        open={showMaterialDialog}
        onOpenChange={setShowMaterialDialog}
      />

      {id && (
        <LinkMaterialDialog
          open={showLinkMaterialDialog}
          onOpenChange={setShowLinkMaterialDialog}
          supplierId={id}
        />
      )}

      {id && (
        <PurchaseOrderFormDialog
          open={showPODialog}
          onOpenChange={setShowPODialog}
          supplierId={id}
        />
      )}

      {/* Status change confirmation dialog (for DELIVERED) */}
      <Dialog
        open={!!statusChangeDialog}
        onOpenChange={() => {
          setStatusChangeDialog(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Entrega</DialogTitle>
            <DialogDescription>
              Marcar o pedido {statusChangeDialog?.orderNumber} como entregue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusChangeDialog(null)}>
              Cancelar
            </Button>
            <Button onClick={confirmStatusChange} disabled={statusMutation.isPending}>
              {statusMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirmar Entrega
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Sub-component: Purchase order expanded details
function PurchaseOrderDetails({
  orderId,
  status,
  orderNumber,
  canEdit,
  onStatusChange,
  onCancel,
  onDelete,
}: {
  orderId: string
  status: string
  orderNumber: string
  canEdit: boolean
  onStatusChange: (orderId: string, newStatus: string, orderNumber: string) => void
  onCancel: (orderId: string) => void
  onDelete: (orderId: string) => void
}) {
  const queryClient = useQueryClient()
  const [showInstallmentsDialog, setShowInstallmentsDialog] = useState(false)
  const [payingInstId, setPayingInstId] = useState<string | null>(null)
  const [payDate, setPayDate] = useState('')
  const [linkingInstId, setLinkingInstId] = useState<string | null>(null)
  const [linkTxId, setLinkTxId] = useState('')

  const { data: orderDetail } = useQuery({
    queryKey: ['purchase-order', orderId],
    queryFn: () => suppliersAPI.getPurchaseOrder(orderId),
  })

  const payMutation = useMutation({
    mutationFn: ({ instId, paidAt }: { instId: string; paidAt?: string }) =>
      suppliersAPI.payInstallment(orderId, instId, { paidAt: paidAt || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-order', orderId] })
      toast.success('Parcela marcada como paga')
      setPayingInstId(null)
      setPayDate('')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const linkMutation = useMutation({
    mutationFn: ({ instId, financialTransactionId }: { instId: string; financialTransactionId: string }) =>
      suppliersAPI.linkInstallment(orderId, instId, { financialTransactionId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-order', orderId] })
      toast.success('Transação vinculada')
      setLinkingInstId(null)
      setLinkTxId('')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const deleteInstMutation = useMutation({
    mutationFn: (instId: string) => suppliersAPI.deleteInstallment(orderId, instId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-order', orderId] })
      toast.success('Parcela removida')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  if (!orderDetail) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
      </div>
    )
  }

  const nextStatus = NEXT_STATUS[status]
  const isFinal = status === 'DELIVERED' || status === 'CANCELLED'

  return (
    <div className="space-y-3">
      {orderDetail.notes && (
        <p className="text-sm text-neutral-600">
          <span className="font-medium">Obs:</span> {orderDetail.notes}
        </p>
      )}

      {orderDetail.deliveryDate && (
        <p className="text-sm text-neutral-600">
          <span className="font-medium">Previsão de entrega:</span>{' '}
          {new Date(orderDetail.deliveryDate).toLocaleDateString('pt-BR')}
        </p>
      )}

      {/* Items table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Material</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead className="text-right">Qtd</TableHead>
            <TableHead className="text-right">Preço Unit.</TableHead>
            <TableHead className="text-right">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orderDetail.items?.map((item: any) => (
            <TableRow key={item.id}>
              <TableCell>{item.material?.name}</TableCell>
              <TableCell>{item.material?.category}</TableCell>
              <TableCell className="text-right">{item.quantity} {item.material?.unit}</TableCell>
              <TableCell className="text-right">R$ {item.unitPrice.toFixed(2)}</TableCell>
              <TableCell className="text-right font-medium">R$ {item.totalPrice.toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Installments */}
      {(() => {
        const installments: any[] = orderDetail.installments ?? []
        const paidTotal = installments
          .filter((i: any) => i.status === 'PAID')
          .reduce((sum: number, i: any) => sum + parseFloat(i.amount), 0)
        const total = parseFloat(orderDetail.totalAmount)

        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">Parcelas</p>
                {installments.length > 0 && (
                  <span className="text-xs text-neutral-500">
                    R$ {paidTotal.toFixed(2)} pago de R$ {total.toFixed(2)}
                  </span>
                )}
              </div>
              {canEdit && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs gap-1"
                  onClick={(e) => { e.stopPropagation(); setShowInstallmentsDialog(true) }}
                >
                  <CreditCard className="h-3.5 w-3.5" />
                  Gerenciar Parcelas
                </Button>
              )}
            </div>

            {installments.length > 0 && (
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-neutral-500">Nº</th>
                      <th className="px-3 py-2 text-left font-medium text-neutral-500">Vencimento</th>
                      <th className="px-3 py-2 text-right font-medium text-neutral-500">Valor</th>
                      <th className="px-3 py-2 text-center font-medium text-neutral-500">Status</th>
                      <th className="px-3 py-2 text-right font-medium text-neutral-500">Ação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {installments.map((inst: any) => {
                      const isOverdue = inst.status === 'PENDING' && new Date(inst.dueDate) < new Date()
                      const displayStatus = inst.status === 'PENDING' && isOverdue ? 'OVERDUE' : inst.status
                      return (
                        <tr key={inst.id}>
                          <td className="px-3 py-2">{inst.installmentNumber}</td>
                          <td className="px-3 py-2">{new Date(inst.dueDate).toLocaleDateString('pt-BR')}</td>
                          <td className="px-3 py-2 text-right">R$ {parseFloat(inst.amount).toFixed(2)}</td>
                          <td className="px-3 py-2 text-center">
                            {displayStatus === 'PAID' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Pago</span>
                            )}
                            {displayStatus === 'PENDING' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Pendente</span>
                            )}
                            {displayStatus === 'OVERDUE' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Vencida</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {inst.status !== 'PAID' && canEdit && (
                              <div className="flex items-center justify-end gap-1">
                                {payingInstId === inst.id ? (
                                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="date"
                                      value={payDate}
                                      onChange={(e) => setPayDate(e.target.value)}
                                      className="h-6 rounded border border-input px-1 text-xs"
                                    />
                                    <Button
                                      size="sm"
                                      className="h-6 px-2 text-xs"
                                      onClick={() => payMutation.mutate({ instId: inst.id, paidAt: payDate || undefined })}
                                      disabled={payMutation.isPending}
                                    >
                                      OK
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" onClick={() => setPayingInstId(null)}>✕</Button>
                                  </div>
                                ) : linkingInstId === inst.id ? (
                                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="text"
                                      value={linkTxId}
                                      onChange={(e) => setLinkTxId(e.target.value)}
                                      placeholder="ID da transação"
                                      className="h-6 rounded border border-input px-1 text-xs w-36"
                                    />
                                    <Button
                                      size="sm"
                                      className="h-6 px-2 text-xs"
                                      onClick={() => linkMutation.mutate({ instId: inst.id, financialTransactionId: linkTxId })}
                                      disabled={linkMutation.isPending || !linkTxId}
                                    >
                                      OK
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" onClick={() => setLinkingInstId(null)}>✕</Button>
                                  </div>
                                ) : (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 px-2 text-xs gap-1"
                                      onClick={(e) => { e.stopPropagation(); setPayingInstId(inst.id); setLinkingInstId(null) }}
                                    >
                                      <CreditCard className="h-3 w-3" /> Pagar
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-xs gap-1"
                                      onClick={(e) => { e.stopPropagation(); setLinkingInstId(inst.id); setPayingInstId(null) }}
                                    >
                                      <Link2 className="h-3 w-3" /> Vincular
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-1 text-xs text-red-400 hover:text-red-600"
                                      onClick={(e) => { e.stopPropagation(); deleteInstMutation.mutate(inst.id) }}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            )}
                            {inst.status === 'PAID' && inst.financialTransaction && (
                              <span className="text-neutral-400 text-xs">
                                {new Date(inst.financialTransaction.date).toLocaleDateString('pt-BR')}
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {showInstallmentsDialog && (
              <InstallmentsDialog
                open={showInstallmentsDialog}
                onClose={() => setShowInstallmentsDialog(false)}
                orderId={orderId}
                totalAmount={total}
                existingInstallments={installments.filter((i: any) => !i.financialTransactionId)}
              />
            )}
          </div>
        )
      })()}

      {/* Actions */}
      {canEdit && (
        <div className="flex gap-2 justify-end pt-2">
          {!isFinal && nextStatus && (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onStatusChange(orderId, nextStatus, orderNumber)
              }}
            >
              {NEXT_STATUS_LABEL[status]}
            </Button>
          )}
          {!isFinal && (
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={(e) => {
                e.stopPropagation()
                onCancel(orderId)
              }}
            >
              Cancelar Pedido
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive"
            onClick={(e) => {
              e.stopPropagation()
              onDelete(orderId)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  )
}
